import React, { useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './dialog';
import { Button } from './button';
import { AlertCircle, CheckCircle2 } from 'lucide-react';

const ERROR_MODAL_DESC_ID = 'error-modal-description';

interface ErrorModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  message: string;
  tips?: string[];
  type?: 'error' | 'warning' | 'info' | 'success';
  showCloseButton?: boolean;
  closeLabel?: string;
}

export const ErrorModal: React.FC<ErrorModalProps> = ({
  isOpen,
  onClose,
  title = 'Error',
  message,
  tips,
  type = 'error',
  showCloseButton = true,
  closeLabel = 'OK'
}) => {
  // Ensure error modal appears above other modals (ConfirmSheet z-index 1000, and any
  // already-open Radix dialog such as the Pay Now / Pay Beneficiary modal). Target the
  // LAST overlay/content in the DOM — the most recently opened dialog, i.e. this one.
  // Using querySelector (first match) would bump the wrong (underlying) dialog and leave
  // the error modal hidden behind it. Run after paint so this dialog's nodes exist.
  useEffect(() => {
    if (!isOpen) return;
    const raf = requestAnimationFrame(() => {
      const overlays = document.querySelectorAll('[data-slot="dialog-overlay"]');
      const lastOverlay = overlays[overlays.length - 1] as HTMLElement | undefined;
      if (lastOverlay) lastOverlay.style.zIndex = '10000';
      const contents = document.querySelectorAll('[data-slot="dialog-content"]');
      const lastContent = contents[contents.length - 1] as HTMLElement | undefined;
      if (lastContent) lastContent.style.zIndex = '10001';
    });
    return () => cancelAnimationFrame(raf);
  }, [isOpen]);
  const getIconColor = () => {
    switch (type) {
      case 'error':
        return 'text-red-500';
      case 'warning':
        return 'text-yellow-500';
      case 'info':
        return 'text-blue-500';
      case 'success':
        return 'text-green-500';
      default:
        return 'text-red-500';
    }
  };

  const getButtonColor = () => {
    switch (type) {
      case 'error':
        return 'bg-red-500 hover:bg-red-600';
      case 'warning':
        return 'bg-yellow-500 hover:bg-yellow-600';
      case 'info':
        return 'bg-blue-500 hover:bg-blue-600';
      case 'success':
        return 'bg-green-500 hover:bg-green-600';
      default:
        return 'bg-red-500 hover:bg-red-600';
    }
  };

  const Icon = type === 'success' ? CheckCircle2 : AlertCircle;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="max-w-sm mx-auto"
        aria-describedby={ERROR_MODAL_DESC_ID}
      >
        <DialogDescription id={ERROR_MODAL_DESC_ID} className="sr-only">
          {message || 'Error dialog content'}
        </DialogDescription>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon className={`w-5 h-5 ${getIconColor()}`} />
            {title}
          </DialogTitle>
        </DialogHeader>
        
        <div className="py-4">
          <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-line">
            {message}
          </p>
          {tips && tips.length > 0 && (
            <div className="mt-4">
              <p className="text-sm font-medium text-gray-800 mb-2">How to fix:</p>
              <ul className="text-sm text-gray-600 space-y-1.5 list-disc pl-4">
                {tips.map((tip) => (
                  <li key={tip}>{tip}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
        
        <div className="flex justify-end">
          <Button
            onClick={onClose}
            className={`${getButtonColor()} text-white`}
          >
            {closeLabel}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
